// Small shared pieces for the order flow: icons, chips, fields, rows.
// Shape rule: inputs and cards 16px (rounded-xl), chips and buttons pill.
import cakesData from '../../data/cakesData.json'
import { sizeOptions } from '../../utils/constants'

const allCakes = [
  ...cakesData.indianFusionCakes,
  ...cakesData.classicCakes,
  ...cakesData.freshFruitCakes,
  ...cakesData.specialCakes,
]
const flavorIdToCakeId = { 'honey-medovik': 'honey-cake' }

/** Menu entry (image, altText, name) for a cake flavour id, or null. */
export const getCakeImage = (flavorId) => {
  if (!flavorId) return null
  const id = flavorIdToCakeId[flavorId] || `${flavorId}-cake`
  return allCakes.find((c) => c.id === id) || null
}

// ── Icons (stroke, 24 grid) ──
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
}
const I = (paths, extra = {}) => {
  const Comp = ({ className = 'w-5 h-5' }) => (
    <svg {...svgProps} {...extra} className={className}>{paths}</svg>
  )
  return Comp
}
export const Icon = {
  Chevron: I(<path d="M9 6l6 6-6 6" />),
  Check: I(<path d="M20 6L9 17l-5-5" />, { strokeWidth: 3 }),
  Arrow: I(<path d="M5 12h14M13 6l6 6-6 6" />, { strokeWidth: 2.25 }),
  Clock: I(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  Calendar: I(<><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M3 10h18M8 3v4M16 3v4" /></>),
  Camera: I(<><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></>),
  Pencil: I(<path d="M4 20l4-1 11-11-3-3L5 16z" />),
  Copy: I(<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a1 1 0 011-1h10" /></>),
  Cake: I(<path d="M4 20h16M5 20v-6a2 2 0 012-2h10a2 2 0 012 2v6M7 12V9a2 2 0 012-2h6a2 2 0 012 2v3M12 7V4" />),
  Cupcake: I(<><path d="M6 11h12l-1.5 9h-9z" /><path d="M5 11a3.5 3.5 0 013-5.5 4 4 0 018 0A3.5 3.5 0 0119 11z" /></>),
  Tag: I(<><path d="M20 12l-8 8-9-9V4h7z" /><circle cx="8" cy="8" r="1.5" /></>),
  Hash: I(<path d="M5 9h14M4 15h14M10 3l-2 18M16 3l-2 18" />),
  Layers: I(<><path d="M12 4l9 5-9 5-9-5z" /><path d="M3 14l9 5 9-5" /></>),
  Leaf: I(<><path d="M5 19c0-8 5-13 14-14-1 9-6 14-14 14z" /><path d="M5 19l7-7" /></>),
  Star: I(<path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" />),
  Info: I(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8v.5" /></>),
  Drop: I(<path d="M12 3s6 6.5 6 11a6 6 0 01-12 0c0-4.5 6-11 6-11z" />),
  Minus: I(<path d="M5 12h14" />, { strokeWidth: 2.25 }),
  Plus: I(<path d="M12 5v14M5 12h14" />, { strokeWidth: 2.25 }),
  WhatsApp: ({ className = 'w-6 h-6' }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
}

// ── Primitives ──
export const chipClass = (on, extra = '') =>
  `inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-full text-sm font-medium border-2 transition-colors whitespace-nowrap ${
    on
      ? 'bg-coral-500 border-coral-500 text-white ring-4 ring-coral-100'
      : 'bg-white border-gray-200 text-gray-700 hover:border-coral-300'
  } disabled:opacity-50 disabled:cursor-not-allowed ${extra}`

export function Chip({ on = false, className = '', children, ...props }) {
  return (
    <button type="button" aria-pressed={on} className={chipClass(on, className)} {...props}>
      {children}
    </button>
  )
}

export const inputClass =
  'w-full min-h-[48px] px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-charcoal placeholder:text-gray-400 focus:border-coral-500 focus:ring-4 focus:ring-coral-100 focus:outline-none transition-colors'

export function SectionTitle({ children, right }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[15px] font-semibold text-charcoal">{children}</span>
      {right && <span className="text-xs text-gray-500">{right}</span>}
    </div>
  )
}

export function Field({ label, right, hint, error, htmlFor, children }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-[15px] font-semibold text-charcoal">{label}</label>
        {right && <span className="text-xs text-gray-500">{right}</span>}
      </div>
      {children}
      {hint && !error && <span className="text-xs text-gray-500">{hint}</span>}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  )
}

export function ErrorText({ children }) {
  return <p className="text-sm text-red-600" role="alert">{children}</p>
}

const noteTones = {
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  calm: 'bg-peach-100 border-peach-300 text-coral-800',
  error: 'bg-red-50 border-red-200 text-red-700',
}
export function Note({ tone = 'amber', icon, children }) {
  return (
    <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border text-[13px] leading-snug ${noteTones[tone]}`}>
      {icon && <span className="shrink-0 mt-px">{icon}</span>}
      <span>{children}</span>
    </div>
  )
}

export function Card({ className = '', children }) {
  return <div className={`bg-white border-2 border-gray-200 rounded-xl ${className}`}>{children}</div>
}

/** One row in an extras list. With onClick it is a button; `open` shows children below. */
export function Row({ icon, title, sub, value, open = false, disabled = false, onClick, right, children }) {
  const body = (
    <>
      {icon && (
        <span className="w-9 h-9 rounded-[10px] bg-peach-100 text-coral-600 flex items-center justify-center shrink-0">
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-medium text-charcoal">{title}</span>
        {sub && <span className="block text-xs text-gray-500 leading-snug mt-0.5">{sub}</span>}
      </span>
      {right ?? (
        <>
          {value && (
            <span className={`text-[13px] font-medium text-right max-w-[45%] leading-snug ${open ? 'text-coral-600' : 'text-gray-600'}`}>
              {value}
            </span>
          )}
          {onClick && !disabled && (
            <Icon.Chevron className={`w-[18px] h-[18px] text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
          )}
        </>
      )}
    </>
  )
  const cls = `w-full flex items-center gap-3 min-h-[60px] px-3.5 py-2.5 text-left ${disabled ? 'opacity-50' : ''}`
  return (
    <div className={open ? 'bg-peach-50' : ''}>
      {onClick ? (
        <button type="button" onClick={onClick} disabled={disabled} className={cls} aria-expanded={children ? open : undefined}>
          {body}
        </button>
      ) : (
        <div className={cls}>{body}</div>
      )}
      {open && children && <div className="px-3.5 pb-4 pt-1 animate-fadeIn">{children}</div>}
    </div>
  )
}

export function Toggle({ on }) {
  return (
    <span aria-hidden="true" className={`relative w-11 h-[26px] rounded-full shrink-0 transition-colors ${on ? 'bg-coral-500' : 'bg-gray-200'}`}>
      <span className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[21px]' : 'left-[3px]'}`} />
    </span>
  )
}

export function PrimaryButton({ className = '', children, ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-coral-500 text-white font-semibold text-[15px] shadow-soft hover:bg-coral-600 hover:shadow-coral active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-coral-500 whitespace-nowrap ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

/** The six cake sizes as "how many people" cards. Read-only without onSelect. */
export function SizeCards({ priceCategory = 'premium', selected, onSelect }) {
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {sizeOptions.map((s) => {
        const on = selected === s.id
        const price = priceCategory === 'classic' ? s.priceClassic : s.pricePremium
        const cls = `relative flex flex-col items-center gap-0.5 rounded-xl border-2 px-1.5 pt-2.5 pb-3 transition-colors ${
          on ? 'bg-coral-500 border-coral-500 text-white ring-4 ring-coral-100' : 'bg-white border-gray-200 text-gray-700'
        } ${onSelect ? 'hover:border-coral-300' : ''}`
        const body = (
          <>
            <span className="absolute top-1.5 right-2 text-[10px] font-semibold opacity-70">{s.id}</span>
            <span className="font-heading text-[22px] font-bold leading-tight">{s.servings}</span>
            <span className="text-xs opacity-85">people</span>
            <span className={`text-sm font-semibold ${on ? 'text-white' : 'text-coral-500'}`}>€{price}</span>
          </>
        )
        return onSelect ? (
          <button key={s.id} type="button" aria-pressed={on} onClick={() => onSelect(s.id)} className={cls}>
            {body}
          </button>
        ) : (
          <div key={s.id} className={cls}>{body}</div>
        )
      })}
    </div>
  )
}
